// Import auth service for authenticated requests
import { authService } from './authService';
import { getServerURL } from './config';

// Tickets Service for comprehensive ticket management
const SERVER_URL = getServerURL();

export const ticketsService = {
  // Test API connectivity
  async testConnection() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      return false;
    }

    try {
      console.log('Testing connection to:', `${SERVER_URL}/tickets`);
      const response = await fetch(`${SERVER_URL}/tickets`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });
      console.log('Connection test response status:', response.status);
      return response.ok;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  },

  // ==================== TICKET MANAGEMENT ====================

  // Get all tickets with filtering and pagination
  async getTickets(filters = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const queryParams = new URLSearchParams();

      // Add all filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v));
          } else {
            queryParams.append(key, value);
          }
        }
      });

      const url = `${SERVER_URL}/tickets?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch tickets');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching tickets:', error);
      throw error;
    }
  },

  // Get single ticket by ID
  async getTicket(ticketId) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch ticket');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  },

  // Create a new ticket
  async createTicket(ticketData) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      console.log('Creating ticket:', ticketData);

      const response = await fetch(`${SERVER_URL}/tickets`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ticketData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create ticket');
      }

      const data = await response.json();
      console.log('Ticket created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  },

  // Update ticket status
  async updateTicketStatus(ticketId, status, reason = null) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, reason }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update ticket status');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating ticket status:', error);
      throw error;
    }
  },

  // Add comment to ticket
  async addComment(ticketId, content, isInternal = false) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}/comments`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content, is_internal: isInternal }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add comment');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  },

  // Add attachment to ticket
  async addAttachment(ticketId, filename, fileUrl, fileSize, mimeType) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}/attachments`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename,
          file_url: fileUrl,
          file_size: fileSize,
          mime_type: mimeType
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add attachment');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error adding attachment:', error);
      throw error;
    }
  },

  // Upload file and attach to ticket
  async uploadAttachment(ticketId, file) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Get auth headers but exclude Content-Type for multipart/form-data
      const authHeaders = authService.getAuthHeaders();
      const headers = {};

      // Only copy Authorization header, NOT Content-Type
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }

      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}/upload`, {
        method: 'POST',
        headers: headers,
        // Don't set Content-Type - browser will set it automatically with boundary for multipart/form-data
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload attachment');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  },

  // Assign ticket to user
  async assignTicket(ticketId, assigneeId) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}/assign`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assignee_id: assigneeId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign ticket');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error assigning ticket:', error);
      throw error;
    }
  },

  // Escalate ticket
  async escalateTicket(ticketId, reason = null) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}/escalate`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to escalate ticket');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error escalating ticket:', error);
      throw error;
    }
  },

  // Update ticket tags
  async updateTicketTags(ticketId, tags, action = 'set') {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}/tags`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tags, action }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update tags');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating tags:', error);
      throw error;
    }
  },

  // Get tickets by assignee
  async getTicketsByAssignee(assigneeId, options = {}) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const queryParams = new URLSearchParams(options);
      const url = `${SERVER_URL}/tickets/assignee/${assigneeId}?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch assignee tickets');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching assignee tickets:', error);
      throw error;
    }
  },

  // Get overdue tickets
  async getOverdueTickets(options = {}) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const queryParams = new URLSearchParams(options);
      const url = `${SERVER_URL}/tickets/overdue?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch overdue tickets');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching overdue tickets:', error);
      throw error;
    }
  },

  // Get ticket statistics
  async getTicketStats() {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/stats`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch ticket stats');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching ticket stats:', error);
      throw error;
    }
  },

  // Delete ticket (soft delete)
  async deleteTicket(ticketId) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/tickets/${ticketId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete ticket');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting ticket:', error);
      throw error;
    }
  },
};
